import i18n from '@i18next/i18n';
import { isAudioElement, isMediaElement, isUiModelIsAttachment, isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import type { IPictureModel } from '../../../Interface/picture-model.interface';
import type { IShapeModel } from '../../../Interface/shape-model.interface';
import { AttachmentClassify, EShowType } from '../constants';
import { AttachmentInfo } from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';

/**
 * 获取当前附件的种类
 * @param uiModel
 * @returns
 */
export function getAttachmentType(uiModel?: IShapeModel | IPictureModel): AttachmentClassify | null {
    if (!uiModel) {
        return null;
    }
    if (isVideoElement(uiModel)) {
        return AttachmentClassify.VIDEO;
    }
    if (isAudioElement(uiModel)) {
        return AttachmentClassify.AUDIO;
    }
    return uiModel.props?.attachment?.classify || null;
}

/**
 * 获取当前附件的展示类型
 * @param uiModel
 * @returns
 */
export function getAttachmentShowType(uiModel?: IShapeModel | IPictureModel): EShowType | null  {
    if (!uiModel) {
        return null;
    }
    if (isVideoElement(uiModel)) {
        return EShowType.PREVIEW;
    }
    if (isAudioElement(uiModel)) {
        return EShowType.PREVIEW;
    }
    return uiModel.props?.attachment?.showType || null;
}

/**
 * 获取当前附件的 objKey
 * @param uiModel
 * @returns
 */
export function getAttachmentObjKey(uiModel?: IShapeModel | IPictureModel): string | null {
    if (!uiModel) {
        return null;
    }
    if (isVideoElement(uiModel)) {
        return (uiModel as IPictureModel)?.video?.link ?? null;
    }
    if (isAudioElement(uiModel)) {
        return (uiModel as IPictureModel)?.audio?.link ?? null;
    }
    return uiModel.props?.attachment.objKey ?? null;
}

/**
 * 获取当前附件的信息
 * @param uiModel
 * @returns
 */
export function getAttachmentInfo(uiModel?: IShapeModel | IPictureModel): AttachmentInfo | null {
    if (!uiModel) {
        return null;
    }
    if (isMediaElement(uiModel)) {
        const { video, audio } = uiModel as IPictureModel;
        const { classify, link, format, size } = video || audio || {};
        const attachmentManager = uiModel?.getAttachmentManager();
        const attachmentId = attachmentManager?.getLocalAttachment(uiModel.id as string)?.id;
        return {
            classify: classify || (video ? AttachmentClassify.VIDEO : AttachmentClassify.AUDIO),
            size,
            name: getMediaAttachmentName(uiModel as IPictureModel),
            objKey: link,
            type: format,
            showType: EShowType.PREVIEW,
            id: attachmentId,
        };
    }
    return uiModel.props.attachment ?? null;
}

/**
 * 判断当前附件是否支持动画，非附件默认都支持
 * 附件中只有音视频预览态支持
 * @param uiModel
 * @returns
 */
export function ifElementSupportAnim(uiModel: IShapeModel | IPictureModel): boolean {
    if (!isUiModelIsAttachment(uiModel)) {
        return true;
    }
    return isMediaElement(uiModel);
}

function getMediaAttachmentName(uiModel: IPictureModel) {
    if (uiModel.video) {
        // 附件二期之前上传的视频 name 在 video 字段中，兼容下之前的逻辑
        return uiModel.name ?? uiModel.video.name ?? i18n.t('视频');
    }
    if (uiModel.audio) {
        // 附件二期之前上传的音频 name 在 audio 字段中，兼容下之前的逻辑
        return uiModel.name ?? uiModel.audio.name ?? i18n.t('音频');
    }
}
